import {
  COMMITMENT_BLOCK_TYPE,
} from "./CommitmentBlock";


type UnknownRecord = Record<string, unknown>;


export interface NormalizedCommitmentBlock {
  id: string;
  type: typeof COMMITMENT_BLOCK_TYPE;
  props: {
    category_slug: string;
    title: string;
    description: string | null;
    default_priority: "high" | "medium" | "low";
    default_complexity: 1 | 2 | 3 | 5;
    due_anchor: "apply_date" | "plan_year_start" | "plan_year_end" | "bor_date" | null;
    due_offset_months: number | null;
    due_offset_days: number | null;
    assigned_job_title: string | null;
    depends_on_block_uids: string[];
    tag_ids: string[];
  };
  children: unknown[];
}


export interface NormalizedPlaybookBlocksJson {
  blocks: Array<NormalizedCommitmentBlock | UnknownRecord>;
}


export function normalizePlaybookBlocksForMaterialization(
  rawBlocks: unknown,
): NormalizedPlaybookBlocksJson {
  const blocks = Array.isArray(rawBlocks)
    ? rawBlocks
    : readBlocksArray(rawBlocks);
  return {
    blocks: blocks.map((block) => normalizeBlock(block)),
  };
}


function readBlocksArray(raw: unknown): unknown[] {
  if (raw && typeof raw === "object") {
    const blocks = (raw as { blocks?: unknown }).blocks;
    if (Array.isArray(blocks)) return blocks;
  }
  return [];
}


function normalizeBlock(block: unknown): NormalizedCommitmentBlock | UnknownRecord {
  if (!block || typeof block !== "object") return {};
  const rec = block as UnknownRecord;
  if (rec.type !== COMMITMENT_BLOCK_TYPE) {
    return {
      ...rec,
      children: Array.isArray(rec.children) ? rec.children : [],
    };
  }
  const props = (rec.props && typeof rec.props === "object")
    ? rec.props as UnknownRecord
    : {};
  return {
    id: String(rec.id || crypto.randomUUID()),
    type: COMMITMENT_BLOCK_TYPE,
    props: {
      category_slug: nonEmptyString(props.category_slug, "service"),
      title: nonEmptyString(props.title, ""),
      description: nullableString(props.description),
      default_priority: priority(props.default_priority),
      default_complexity: complexity(props.default_complexity),
      due_anchor: dueAnchor(props.due_anchor),
      due_offset_months: nullableNumber(props.due_offset_months),
      due_offset_days: nullableNumber(props.due_offset_days),
      assigned_job_title: nullableString(props.assigned_job_title),
      depends_on_block_uids: dependencyList(props.depends_on_block_uids),
      tag_ids: stringList(props.tag_ids),
    },
    children: Array.isArray(rec.children) ? rec.children : [],
  };
}


function nonEmptyString(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}


function nullableString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}


function nullableNumber(value: unknown): number | null {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}


function priority(value: unknown): "high" | "medium" | "low" {
  return value === "high" || value === "medium" || value === "low"
    ? value
    : "low";
}


function complexity(value: unknown): 1 | 2 | 3 | 5 {
  return value === 1 || value === 2 || value === 3 || value === 5
    ? value
    : 1;
}


function dueAnchor(
  value: unknown,
): "apply_date" | "plan_year_start" | "plan_year_end" | "bor_date" | null {
  if (
    value === "apply_date" ||
    value === "plan_year_start" ||
    value === "plan_year_end" ||
    value === "bor_date"
  ) {
    return value;
  }
  return null;
}


function dependencyList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value.split(",").map((v) => v.trim()).filter(Boolean);
  }
  return [];
}


function stringList(value: unknown): string[] {
  if (typeof value === "string") {
    return value.split(",").map((v) => v.trim()).filter(Boolean);
  }
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v).trim()).filter(Boolean);
}
